import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, map, switchMap, of, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';

/** Lo que puede hacer alguien. Los codigos los define PermisoService. */
export type Permiso =
  | 'ver_dashboard'
  | 'editar_proyecto'
  | 'ver_monitor'
  | 'reprocesar'
  | 'ver_configuracion'
  | 'administrar_usuarios'
  | 'cerrar_periodo'
  /* El reporte de salud: mientras se valida, solo una lista piloto (3/11/2026) */
  | 'ver_reporte_salud';

export interface Sesion {
  id: number;
  nombre: string | null;
  correo: string | null;
  roles: string[];
  permisos: Permiso[];
  /**
   * Si el backend ya aplica los roles.
   *
   * Con el interruptor apagado, `permisos` viene vacio pero se puede todo. Sin
   * este dato la pantalla no podria distinguir "no tiene permisos" de "los
   * roles todavia no se aplican", y escondería el menú entero.
   */
  roles_activos: boolean;
}

/**
 * Quien esta adentro y que puede.
 *
 * Se pide una sola vez y lo miran el menu y las pantallas. El backend vuelve a
 * verificar todo: esto solo decide que se muestra, no que se permite.
 */
@Injectable({ providedIn: 'root' })
export class SesionService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  readonly sesion = signal<Sesion | null>(null);
  readonly cargada = signal(false);

  private pedida = false;
  private readonly cargada$ = toObservable(this.cargada);

  /**
   * Cuando ya se sabe quién es. Lo esperan los guards de las rutas, que corren
   * antes de que exista el layout que pide la sesión: por eso la pide si
   * todavía nadie lo hizo.
   */
  lista(): Observable<void> {
    this.cargar();

    return this.cargada$.pipe(filter(Boolean), take(1), map(() => undefined));
  }

  cargar(): void {
    // Una sola vez: la piden el layout y los guards
    if (this.pedida) return;
    this.pedida = true;

    this.auth
      .tokenParaPedir()
      .pipe(
        switchMap((token) => {
          if (!token) return of(null);

          return this.http.get<{ success: boolean; data: Sesion }>(
            `${environment.accountingApi.baseUrl}/api/me`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        })
      )
      .subscribe({
        next: (r) => {
          if (r?.success) this.sesion.set(r.data);
          this.cargada.set(true);
        },
        // Si /me no contesta se sigue mostrando todo: es preferible a dejar a
        // alguien sin menu por una caida del backend. El backend igual corta.
        error: () => this.cargada.set(true)
      });
  }

  /**
   * Si esta persona puede ver algo.
   *
   * **Los roles se aplican siempre.** Antes había un escape —«mientras los
   * roles no se apliquen, se puede todo»— atado a un interruptor que nunca se
   * prendió: todo el mundo veía todos los menús, incluido quien sólo gestiona
   * proyectos.
   *
   * **Antes de saber quién es, sí se muestra todo**: el menú aparece completo
   * durante el instante que tarda `/me`, y se acota cuando llega. Al revés
   * —esconder hasta saber— el menú parpadearía vacío en cada carga, y el
   * candado de verdad no está acá sino en el backend, que responde 403 aunque
   * alguien llegue por la URL.
   */
  puede(permiso: Permiso): boolean {
    const s = this.sesion();

    if (!s) return true;

    return s.permisos.includes(permiso);
  }

  readonly roles = computed(() => this.sesion()?.roles ?? []);
}
